(ns shared.core.components.recharts.label
  (:require [shared.core.style :as style]
            [shared.core.util :as util]
            [shared.core.reactive :as re]
            [shared.core.components.recharts.util :as recharts.util]))

(defn truncate-text
  "Truncates text if longer than max-length and adds ellipsis"
  [text max-length]
  (let [text (str text)]
    (if (> (count text) max-length)
      (str (subs text 0 max-length) "...")
      text)))

(defn text-label
  "Custom label renderer for pie chart slices with rotated text.
   Only renders labels for slices >= 1% of the total."
  [props {:keys [label-fn fill name-key]}]
  (let [fill (or fill (style/alc-color "Text/primary"))
        {:keys [name label label-override cx cy midAngle innerRadius outerRadius
                percent]
         :as args} (js->clj props :keywordize-keys true)
        name (if name-key
               (or (get args name-key) (get args (keyword name-key)))
               name)
        ;; Only show label if slice is 1% or larger
        percentage (* percent 100)]
    (when (>= percentage 1)
      (let [RADIAN (/ js/Math.PI 180)
            radius (+ innerRadius (/ (- outerRadius innerRadius) 2))
            x (+ cx (* radius (js/Math.cos (* (- midAngle) RADIAN))))
            y (+ cy (* radius (js/Math.sin (* (- midAngle) RADIAN))))

            ;; Calculate rotation angle
            angle' (- 180 midAngle)
            angle'' (if (< angle' 0) (+ angle' 360) angle')
            angle (if (and (> angle'' 90) (< angle'' 270))
                    (- angle'' 180)
                    angle'')

            ;; Truncate text based on available space
            ;; Approximate max characters based on slice size (more conservative)
            max-chars (Math/floor (* percentage 0.4))
            capitalized-label (or label-override
                                  (when label-fn
                                    (label-fn (or label name)))
                                  (util/->capitalize-words (or label name)))
            truncated-label (truncate-text capitalized-label (max max-chars 6))]
        (re/as-element
          [:text {:x x
                  :y y
                  :fill fill
                  :text-anchor "middle"
                  :font-family style/roboto-font-family
                  :dominant-baseline :central
                  :font-size 10
                  :font-weight :normal
                  :pointer-events "none"
                  :transform (str "rotate(" angle ", " x ", " y ")")}
           truncated-label])))))

(defn fit-center-text
  "Type sizes for a pie chart's center label. `scale` is how much of the chart's full
   size it is being drawn at (1 = full, so a full-size chart keeps the sizes these were
   designed as) and `hole-size` is the hole's diameter in px. Each size has a floor: in
   a small chart legibility matters more than filling the hole exactly."
  [{:keys [scale hole-size value-str]}]
  (let [hole (or hole-size 0)
        scale (min 1 (or scale 1))
        scaled (fn [base floor] (max floor (js/Math.round (* base scale))))
        name-font (scaled 14 9)
        value-font (scaled 32 14)
        ;; The headline also has to fit ACROSS the hole, which scale alone can't
        ;; promise — a seven-figure total is three times as wide as a three-figure one.
        usable-width (* 0.8 hole)
        measured (when (and (pos? usable-width) (not-empty (str value-str)))
                   (recharts.util/measure-text-width
                    value-str
                    (str style/font-bold-weight " " value-font "px " style/roboto-font-family)))]
    {:value-font (if (and measured (> measured usable-width))
                   (max 12 (js/Math.floor (* value-font (/ usable-width measured))))
                   value-font)
     :percentage-font (scaled 16 10)
     :name-font name-font
     :name-max-width (max 70 (js/Math.round (* 140 scale)))
     ;; Two lines of name, then clip — a long tag value must not spill over the ring.
     :name-max-height (js/Math.round (* 2.4 name-font))}))

(defn center-label
  "Center label showing total count or hovered item, sized to fit the donut hole it
   sits in (see `fit-center-text` for `scale`/`hole-size`)."
  [{:keys [total hovered-item-data name-key value-key scale hole-size]}]
  (let [hovered-value (when hovered-item-data
                        (or (when value-key (get hovered-item-data value-key))
                            (:value hovered-item-data)
                            (get hovered-item-data "value")
                            0))
        value-str (.toLocaleString (or hovered-value total 0))
        {:keys [value-font percentage-font name-font name-max-width name-max-height]}
        (fit-center-text {:scale scale
                          :hole-size hole-size
                          :value-str value-str})
        value-style {:font-size value-font
                     :margin-bottom 4
                     :font-weight style/font-bold-weight
                     :color (style/alc-color "Text/primary")
                     :letter-spacing "0.41px"
                     :line-height "100%"}
        name-style {:font-size name-font
                    :font-weight style/font-regular-weight
                    :color (style/alc-color "Text/secondary")
                    :text-transform :uppercase
                    :letter-spacing "0px"
                    :line-height 1.2
                    :max-width name-max-width
                    :max-height name-max-height
                    :overflow :hidden
                    :margin "0 auto"}]
    [:div {:style {:position :absolute
                   :top "50%"
                   :left "50%"
                   :transform "translate(-50%, -50%)"
                   :text-align :center
                   :pointer-events :none
                   :z-index 1
                   :font-family style/roboto-font-family
                   :color (style/alc-color "Text/primary")}}
     (if hovered-item-data
       (let [name (get hovered-item-data (or name-key :name))
             percentage (recharts.util/calculate-percentage hovered-value total)]
         [:<>
          [:div {:style value-style}
           value-str]
          [:div {:style {:font-size percentage-font
                         :font-weight style/font-regular-weight
                         :color (style/alc-color "Text/primary")
                         :margin-bottom 4}}
           (str "(" (.toFixed percentage 1) "%)")]
          [:div {:style name-style}
           (util/->capitalize-words name)]])
       [:<>
        [:div {:style value-style}
         value-str]
        [:div {:style name-style}
         "TOTAL"]])]))